/**
 * Summary Parser — DSN v2 ("Discovery State Notation")
 *
 * Deterministic parser that reads a DSN-formatted memory summary and produces
 * a DsnModel for the story graph and narrative visualizations.
 *
 * DSN lines are prefixed with `### ` (triple-hash comment lines). The parser
 * strips that prefix, then dispatches on the first sigil character:
 *
 *   %key value         → Session header fields
 *   %narrative … %end-narrative → Narrative prose block
 *   %history … %end-history     → Session history entries
 *   @Actor :role ~m "note"       → Actor definitions
 *   $Term = "def" ~m             → Glossary entries
 *   %sys Name :type ~m "desc"    → Systems
 *   [seq] actor > action > target ~m |cond| "note" → Flow steps
 *   %variant Name … %end-variant → Process variants
 *   &Object {attrs} ~m "note"    → Work objects
 *   !rule Name ~m "desc"         → Business rules
 *   >>Event ~m {attrs} "note"    → Domain events
 *   ?? Question #tag @actor pri  → Open questions
 *   ✗✗ "what" → "why" #tag      → Rejected alternatives
 *   ~~ Assumption #tag           → Assumptions
 */

// ─── DSN Model Types ─────────────────────────────────────────────────

export type Maturity = 'confirmed' | 'assumed' | 'uncertain' | 'disputed';

export interface DsnActor {
  name: string;
  role: string;
  maturity: Maturity;
  note: string;
}

export interface DsnFlowStep {
  seq: string;           // e.g. "1", "1.1", "2a"
  actor: string;
  intent: 'command' | 'query' | 'event' | 'reaction' | 'action' | '';
  action: string;
  target: string;
  viaSystem: string;     // from [via %sys SystemName]
  phase: string;         // from === Phase Name ===
  maturity: Maturity;
  condition: string;
  note: string;
  variantName?: string;  // set when step belongs to a variant
}

export interface DsnGlossaryEntry {
  term: string;
  definition: string;
  maturity: Maturity;
}

export interface DsnSystem {
  name: string;
  type: 'internal' | 'external' | 'touchpoint' | 'unknown';
  contextName: string;   // from @ContextName
  maturity: Maturity;
  description: string;
  integratesWith: string[];
}

export interface DsnWorkObject {
  name: string;
  contextName: string;   // from @ContextName
  attributes: string[];
  maturity: Maturity;
  note: string;
}

export interface DsnBusinessRule {
  name: string;
  maturity: Maturity;
  condition: string;
  consequence: string;
  note: string;
}

export interface DsnEvent {
  name: string;
  contextName: string;   // from @ContextName
  maturity: Maturity;
  attributes: string[];
  note: string;
}

export interface DsnQuestion {
  text: string;
  tag: string;
  actor: string;
  priority: 'blocking' | 'important' | 'nice-to-know';
  answered?: string;  // set if ✔✔ answered
}

export interface DsnRejected {
  proposal: string;
  reason: string;
  tag: string;
}

export interface DsnAssumption {
  text: string;
  tag: string;
  status: 'open' | 'confirmed' | 'rejected';
}

export interface DsnVariant {
  name: string;
  maturity: Maturity;
  trigger: string;
  description: string;
  steps: DsnFlowStep[];
}

export interface DsnBoundedContext {
  name: string;
  maturity: Maturity;
  description: string;
}

export interface DsnSessionHeader {
  context: string;
  session: string;
  focus: string;
  status: string;
  pending: string;
  started: string;
  updated: string;
}

export interface DsnHistoryEntry {
  sessionNumber: number;
  summary: string;
  topics: string[];
}

export interface DsnModel {
  header: DsnSessionHeader;
  narrative: string;
  history: DsnHistoryEntry[];
  actors: DsnActor[];
  glossary: DsnGlossaryEntry[];
  boundedContexts: DsnBoundedContext[];
  systems: DsnSystem[];
  flow: DsnFlowStep[];
  variants: DsnVariant[];
  workObjects: DsnWorkObject[];
  businessRules: DsnBusinessRule[];
  events: DsnEvent[];
  questions: DsnQuestion[];
  rejected: DsnRejected[];
  assumptions: DsnAssumption[];
}

// ── Re-export old FlowModel types for backward compat ────────────────

export type StepStatus = 'draft' | 'reviewed' | 'confirmed';

export interface FlowStep {
  id: string;
  label: string;
  processGroup: string;
  actor: string;
  order: number;
}

export interface FlowConnection {
  id: string;
  fromStepId: string;
  toStepId: string;
  type: 'sequential' | 'cross-lane' | 'cross-process';
  label?: string;
}

export interface FlowLane {
  actor: string;
  order: number;
}

export interface FlowProcessGroup {
  name: string;
  status: StepStatus;
  steps: FlowStep[];
  results: string[];
  rules: string[];
}

export interface FlowGap {
  type: 'no-actor' | 'no-result' | 'no-trigger' | 'no-rules';
  processGroup: string;
  description: string;
}

export interface FlowModel {
  domainName: string;
  lanes: FlowLane[];
  processGroups: FlowProcessGroup[];
  connections: FlowConnection[];
  gaps: FlowGap[];
}

// ─── Main Parser ─────────────────────────────────────────────────────

/**
 * Parse a DSN memory-summary string into a DsnModel.
 */
export function parseDsn(raw: string): DsnModel {
  const model: DsnModel = emptyDsnModel();
  if (!raw || !raw.trim()) return model;

  // Strip the `### ` prefix from every line. Also handle lines that are just `###`.
  const lines = raw.split('\n').map((line) => {
    if (line.startsWith('### ')) return line.slice(4);
    if (line === '###') return '';
    return line;
  });

  // Track the current phase for flow steps
  let currentPhase = '';

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    // Skip blanks, decorative lines (pure ====)
    if (!line || /^=+$/.test(line)) { i++; continue; }

    // ── Phase header: === Phase Name ===
    const phaseMatch = line.match(/^===\s+(.+?)\s+===$/);
    if (phaseMatch) {
      currentPhase = phaseMatch[1];
      i++; continue;
    }

    // Skip section titles like "1. SESSION HEADER …" etc.
    if (/^\d+\.\s+[A-Z]/.test(line)) { i++; continue; }
    // Skip instruction/Purpose/Rules/Types comment lines
    if (/^(Purpose:|Rules:|Types:|Maturity|Graduation|Syntax:|Sequence|Branching|Conditions|Target|─|The AI|Priority:|Questions|This section|Each entry|Examples?:)/i.test(line)) { i++; continue; }
    if (/^-\s+(Updated by|Should be|Written in|Must capture|Log |Prefer |Only create|When a|Never |Insert |Add |If a|If the|Use )/i.test(line)) { i++; continue; }

    // ── Session header %key
    if (line.startsWith('%context')) { model.header.context = valAfter(line, '%context'); i++; continue; }
    if (line.startsWith('%session')) { model.header.session = valAfter(line, '%session'); i++; continue; }
    if (line.startsWith('%focus')) { model.header.focus = valAfter(line, '%focus'); i++; continue; }
    if (line.startsWith('%status')) { model.header.status = valAfter(line, '%status'); i++; continue; }
    if (line.startsWith('%pending')) { model.header.pending = valAfter(line, '%pending'); i++; continue; }
    if (line.startsWith('%started')) { model.header.started = valAfter(line, '%started'); i++; continue; }
    if (line.startsWith('%updated')) { model.header.updated = valAfter(line, '%updated'); i++; continue; }

    // ── Narrative block
    if (line.startsWith('%narrative')) {
      i++;
      const buf: string[] = [];
      while (i < lines.length && !lines[i].trim().startsWith('%end-narrative')) {
        buf.push(lines[i].startsWith('### ') ? lines[i].slice(4) : lines[i]);
        i++;
      }
      model.narrative = buf.join('\n').trim();
      i++; // skip %end-narrative
      continue;
    }

    // ── History block
    if (line.startsWith('%history')) {
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('%end-history')) {
        const hLine = lines[i].trim();
        const hMatch = hLine.match(/^S(\d+):\s*"([^"]*)"(?:\s*\[([^\]]*)\])?/);
        if (hMatch) {
          model.history.push({
            sessionNumber: parseInt(hMatch[1], 10),
            summary: hMatch[2],
            topics: hMatch[3] ? hMatch[3].split(/,\s*/) : [],
          });
        }
        i++;
      }
      i++; // skip %end-history
      continue;
    }

    // ── Actor: @Name :role ~m "note"
    if (line.startsWith('@')) {
      const actor = parseActor(line);
      if (actor) model.actors.push(actor);
      i++; continue;
    }

    // ── Glossary: $Term = "def" ~m
    if (line.startsWith('$')) {
      const entry = parseGlossary(line);
      if (entry) model.glossary.push(entry);
      i++; continue;
    }

    // ── Bounded context: %bc ContextName ~m "desc"
    if (line.startsWith('%bc ')) {
      const bc = parseBoundedContext(line);
      if (bc) model.boundedContexts.push(bc);
      i++; continue;
    }

    // ── System: %sys Name :type @ContextName ~m "desc" [integrates]
    if (line.startsWith('%sys ')) {
      const sys = parseSystem(line);
      if (sys) model.systems.push(sys);
      i++; continue;
    }

    // ── Flow step: [seq] actor > (intent) action > target [via %sys System] ~m |cond| "note"
    if (line.startsWith('[')) {
      const step = parseFlowStep(line, currentPhase);
      if (step) model.flow.push(step);
      i++; continue;
    }

    // ── Variant: %variant Name ~m |trigger| "desc" then flow steps then %end-variant
    if (line.startsWith('%variant ')) {
      const variant = parseVariantHeader(line);
      let variantPhase = currentPhase;
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('%end-variant')) {
        const vLine = lines[i].trim();
        const vPhaseMatch = vLine.match(/^===\s+(.+?)\s+===$/);
        if (vPhaseMatch) {
          variantPhase = vPhaseMatch[1];
        } else if (vLine.startsWith('[')) {
          const step = parseFlowStep(vLine, variantPhase);
          if (step) {
            step.variantName = variant.name;
            variant.steps.push(step);
          }
        }
        i++;
      }
      model.variants.push(variant);
      i++; // skip %end-variant
      continue;
    }

    // ── Work object: &Name @ContextName {attrs} ~m "note"
    if (line.startsWith('&')) {
      const wo = parseWorkObject(line);
      if (wo) model.workObjects.push(wo);
      i++; continue;
    }

    // ── Business rule: !rule Name ~m …
    if (line.startsWith('!rule ')) {
      const rule = parseBusinessRule(line);
      if (rule) model.businessRules.push(rule);
      i++; continue;
    }

    // ── Event: >>Name @ContextName ~m {attrs} "note" OR Name ~m {attrs} "note"
    if (line.startsWith('>>') || /^[A-Za-z][\w-]*\s+(@\S+\s+)?[✔~?✗]/.test(line)) {
      const ev = parseEvent(line);
      if (ev) model.events.push(ev);
      i++; continue;
    }

    // ── Answered question: ✔✔ text #tag -> "answer"
    if (line.startsWith('✔✔')) {
      const aq = parseAnsweredQuestion(line);
      if (aq) model.questions.push(aq);
      i++; continue;
    }

    // ── Open question: ?? text #tag @actor priority
    if (line.startsWith('??')) {
      const q = parseQuestion(line);
      if (q) model.questions.push(q);
      i++; continue;
    }

    // ── Rejected: ✗✗ "what" -> "why" #tag
    if (line.startsWith('✗✗')) {
      const rej = parseRejected(line);
      if (rej) model.rejected.push(rej);
      i++; continue;
    }

    // ── Assumption: ~~ text #tag
    if (line.startsWith('~~')) {
      const assum = parseAssumption(line);
      if (assum) model.assumptions.push(assum);
      i++; continue;
    }

    // ── Maturity summary fields (informational, captured in header)
    if (line.startsWith('%maturity') || line.startsWith('%ready-to-graduate') || line.startsWith('%blocked-by')) {
      // Informational – skip for now
      i++; continue;
    }

    // Unrecognised line – skip
    i++;
  }

  return model;
}

/**
 * Legacy compat: parse a memory summary into the old FlowModel shape.
 */
export function parseSummary(markdown: string): FlowModel {
  const dsn = parseDsn(markdown);
  return dsnToFlowModel(dsn);
}

// ─── DSN → FlowModel Bridge ─────────────────────────────────────────

function dsnToFlowModel(dsn: DsnModel): FlowModel {
  const model: FlowModel = {
    domainName: dsn.header.context || '',
    lanes: [],
    processGroups: [],
    connections: [],
    gaps: [],
  };

  // Lanes from actors
  const actorSet = new Set<string>();
  for (const a of dsn.actors) actorSet.add(a.name);
  for (const s of dsn.flow) { if (s.actor) actorSet.add(s.actor); }

  let order = 0;
  for (const actor of actorSet) {
    model.lanes.push({ actor, order: order++ });
  }

  // Group sequential flow steps into process groups (split when actor changes)
  if (dsn.flow.length > 0) {
    let currentGroup: FlowProcessGroup | null = null;
    let stepCounter = 0;

    for (const fs of dsn.flow) {
      // Start a new group when actor changes or this is the first step
      if (!currentGroup || currentGroup.steps[0]?.actor !== fs.actor) {
        currentGroup = {
          name: fs.action,
          status: maturityToStatus(fs.maturity),
          steps: [],
          results: [],
          rules: fs.condition ? [fs.condition] : [],
        };
        model.processGroups.push(currentGroup);
      }

      currentGroup.steps.push({
        id: `step-${stepCounter++}`,
        label: `${fs.actor} → ${fs.action} → ${fs.target}`,
        processGroup: currentGroup.name,
        actor: fs.actor,
        order: currentGroup.steps.length,
      });

      if (fs.note) currentGroup.results.push(fs.note);
    }

    // Sequential connections
    const allSteps = model.processGroups.flatMap((g) => g.steps);
    for (let i = 0; i < allSteps.length - 1; i++) {
      model.connections.push({
        id: `conn-${allSteps[i].id}-${allSteps[i + 1].id}`,
        fromStepId: allSteps[i].id,
        toStepId: allSteps[i + 1].id,
        type: allSteps[i].actor === allSteps[i + 1].actor ? 'sequential' : 'cross-lane',
      });
    }
  }

  return model;
}

function maturityToStatus(m: Maturity): StepStatus {
  if (m === 'confirmed') return 'confirmed';
  if (m === 'assumed') return 'reviewed';
  return 'draft';
}

// ─── Line Parsers ────────────────────────────────────────────────────

function valAfter(line: string, prefix: string): string {
  // "%context   — Org/BoundedContext" → strip prefix, optional dashes/delimiters
  return line.slice(prefix.length).replace(/^\s*[—\-:]\s*/, '').trim();
}

function parseMaturity(token: string | undefined): Maturity {
  if (!token) return 'assumed';
  const t = token.trim();
  if (t === '✔') return 'confirmed';
  if (t === '~') return 'assumed';
  if (t === '?') return 'uncertain';
  if (t === '✗') return 'disputed';
  return 'assumed';
}

function extractQuoted(text: string): string {
  const m = text.match(/"([^"]*)"/);
  return m ? m[1] : '';
}

function extractCondition(text: string): string {
  const m = text.match(/\|([^|]*)\|/);
  return m ? m[1] : '';
}

function parseActor(line: string): DsnActor | null {
  // @ActorName :role ~maturity "note"
  const m = line.match(/^@(\S+)\s+:(\S+)\s+([✔~?✗])\s*(?:"([^"]*)")?/);
  if (!m) {
    // Simpler form: @ActorName :role
    const simple = line.match(/^@(\S+)\s+:(\S+)/);
    if (simple) {
      return { name: simple[1], role: simple[2], maturity: 'assumed', note: '' };
    }
    // Simplest form: @ActorName
    const simplest = line.match(/^@(\S+)/);
    if (simplest) {
      return { name: simplest[1], role: '', maturity: 'assumed', note: '' };
    }
    return null;
  }
  return { name: m[1], role: m[2], maturity: parseMaturity(m[3]), note: m[4] || '' };
}

function parseGlossary(line: string): DsnGlossaryEntry | null {
  // $Term = "definition" ~maturity
  const m = line.match(/^\$(\S+)\s*=\s*"([^"]*)"\s*([✔~?✗])?/);
  if (!m) return null;
  return { term: m[1], definition: m[2], maturity: parseMaturity(m[3]) };
}

function parseBoundedContext(line: string): DsnBoundedContext | null {
  // %bc ContextName ~maturity "description"
  const m = line.match(/^%bc\s+(\S+)\s+([✔~?✗])\s*(?:"([^"]*)")?/);
  if (!m) {
    const simple = line.match(/^%bc\s+(\S+)/);
    if (simple) return { name: simple[1], maturity: 'assumed', description: '' };
    return null;
  }
  return { name: m[1], maturity: parseMaturity(m[2]), description: m[3] || '' };
}

function parseSystem(line: string): DsnSystem | null {
  // %sys SystemName :type @ContextName ~maturity "description" [integrates-with]
  const normalizeSystemType = (rawType: string): DsnSystem['type'] => {
    const t = String(rawType || '').trim().toLowerCase();
    if (t === 'internal') return 'internal';
    if (t === 'external') return 'external';
    if (t === 'touchpoint') return 'touchpoint';
    return 'unknown';
  };
  // Full form with optional @ContextName
  const m = line.match(/^%sys\s+(\S+)\s+:(\S+)\s+(?:@(\S+)\s+)?([✔~?✗])\s*(?:"([^"]*)")?\s*(?:\[([^\]]*)\])?/);
  if (!m) {
    const simple = line.match(/^%sys\s+(\S+)\s+:(\S+)/);
    if (simple) {
      return { name: simple[1], type: normalizeSystemType(simple[2]), contextName: '', maturity: 'assumed', description: '', integratesWith: [] };
    }
    return null;
  }
  return {
    name: m[1],
    type: normalizeSystemType(m[2]),
    contextName: m[3] || '',
    maturity: parseMaturity(m[4]),
    description: m[5] || '',
    integratesWith: m[6] ? m[6].split(/,\s*/) : [],
  };
}

const VALID_INTENTS = new Set(['command', 'query', 'event', 'reaction', 'action']);
type FlowIntent = DsnFlowStep['intent'];

/** Extract (intent) prefix from action text and return [intent, cleanAction] */
function extractIntent(rawAction: string): [FlowIntent, string] {
  const m = rawAction.match(/^\((\w+)\)\s*(.*)/);
  if (m && VALID_INTENTS.has(m[1].toLowerCase())) {
    return [m[1].toLowerCase() as FlowIntent, m[2].trim()];
  }
  return ['', rawAction];
}

/** Extract [via %sys SystemName] from the target portion and return [cleanTarget, viaSystem] */
function extractViaSystem(rawTarget: string): [string, string] {
  const m = rawTarget.match(/^(.+?)\s+\[via\s+%sys\s+(\S+)\]$/);
  if (m) return [m[1].trim(), m[2]];
  return [rawTarget, ''];
}

function parseFlowStep(line: string, currentPhase: string): DsnFlowStep | null {
  // [seq] actor > (intent) action > target [via %sys System] ~maturity |condition| "note"
  const m = line.match(/^\[([^\]]+)\]\s+(\S+)\s*>\s*(.+?)\s*>\s*(.+?)\s+([✔~?✗])\s*(?:\|([^|]*)\|)?\s*(?:"([^"]*)")?/);
  if (!m) {
    // Looser parse: [seq] actor > action > target
    const loose = line.match(/^\[([^\]]+)\]\s+(\S+)\s*>\s*(.+?)\s*>\s*(\S+)/);
    if (loose) {
      const [intent, action] = extractIntent(loose[3].trim());
      const [target, viaSystem] = extractViaSystem(loose[4].trim());
      return {
        seq: loose[1].trim(),
        actor: loose[2].replace(/^@+/, ''),
        intent,
        action,
        target,
        viaSystem,
        phase: currentPhase,
        maturity: 'assumed',
        condition: extractCondition(line),
        note: extractQuoted(line),
      };
    }
    return null;
  }
  const [intent, action] = extractIntent(m[3].trim());
  const [target, viaSystem] = extractViaSystem(m[4].trim());
  return {
    seq: m[1].trim(),
    actor: m[2].replace(/^@+/, ''),
    intent,
    action,
    target,
    viaSystem,
    phase: currentPhase,
    maturity: parseMaturity(m[5]),
    condition: m[6] || '',
    note: m[7] || '',
  };
}

function parseVariantHeader(line: string): DsnVariant {
  // %variant VariantName ~maturity |trigger condition| "description"
  const m = line.match(/^%variant\s+(\S+)\s+([✔~?✗])\s*(?:\|([^|]*)\|)?\s*(?:"([^"]*)")?/);
  if (!m) {
    const simple = line.match(/^%variant\s+(\S+)/);
    return {
      name: simple ? simple[1] : 'Unknown',
      maturity: 'assumed',
      trigger: '',
      description: '',
      steps: [],
    };
  }
  return {
    name: m[1],
    maturity: parseMaturity(m[2]),
    trigger: m[3] || '',
    description: m[4] || '',
    steps: [],
  };
}

function parseWorkObject(line: string): DsnWorkObject | null {
  // &ObjectName @ContextName {known attributes} ~maturity "note"
  const m = line.match(/^&(\S+)\s+(?:@(\S+)\s+)?\{([^}]*)\}\s*([✔~?✗])?\s*(?:"([^"]*)")?/);
  if (!m) {
    // Simpler: &ObjectName @ContextName or just &ObjectName
    const simple = line.match(/^&(\S+)(?:\s+@(\S+))?/);
    if (simple) return { name: simple[1], contextName: simple[2] || '', attributes: [], maturity: 'assumed', note: '' };
    return null;
  }
  return {
    name: m[1],
    contextName: m[2] || '',
    attributes: m[3] ? m[3].split(/,\s*/).map((a) => a.trim()).filter(Boolean) : [],
    maturity: parseMaturity(m[4]),
    note: m[5] || '',
  };
}

function parseBusinessRule(line: string): DsnBusinessRule | null {
  // !rule RuleName ~maturity |condition| -> consequence "note"
  const m = line.match(/^!rule\s+(\S+)\s+([✔~?✗])\s*(?:\|([^|]*)\|\s*->\s*(.+?))?\s*(?:"([^"]*)")?$/);
  if (!m) {
    const simple = line.match(/^!rule\s+(\S+)\s+([✔~?✗])?\s*(?:"([^"]*)")?/);
    if (simple) {
      return { name: simple[1], maturity: parseMaturity(simple[2]), condition: '', consequence: '', note: simple[3] || '' };
    }
    return null;
  }
  return {
    name: m[1],
    maturity: parseMaturity(m[2]),
    condition: m[3] || '',
    consequence: m[4]?.trim() || '',
    note: m[5] || '',
  };
}

function parseEvent(line: string): DsnEvent | null {
  // >>EventName @ContextName ~maturity {known attrs} "note"
  const m = line.match(/^>>(\S+)\s+(?:@(\S+)\s+)?([✔~?✗])\s*(?:\{([^}]*)\})?\s*(?:"([^"]*)")?/);
  if (!m) {
    // Fact style (without >>): EventName @ContextName ~maturity {known attrs} "note"
    const fact = line.match(/^([A-Za-z][\w-]*)\s+(?:@(\S+)\s+)?([✔~?✗])\s*(?:\{([^}]*)\})?\s*(?:"([^"]*)")?/);
    if (fact) {
      return {
        name: fact[1],
        contextName: fact[2] || '',
        maturity: parseMaturity(fact[3]),
        attributes: fact[4] ? fact[4].split(/,\s*/).map((a) => a.trim()).filter(Boolean) : [],
        note: fact[5] || '',
      };
    }
    const simple = line.match(/^>>(\S+)/);
    if (simple) return { name: simple[1], contextName: '', maturity: 'assumed', attributes: [], note: '' };
    return null;
  }
  return {
    name: m[1],
    contextName: m[2] || '',
    maturity: parseMaturity(m[3]),
    attributes: m[4] ? m[4].split(/,\s*/).map((a) => a.trim()).filter(Boolean) : [],
    note: m[5] || '',
  };
}

function parseQuestion(line: string): DsnQuestion | null {
  // ?? QuestionText #tag @relevant-actor priority
  const m = line.match(/^\?\?\s+(.+?)\s+#(\S+)\s+@(\S+)\s+(!!|!|\.)/);
  if (!m) {
    // Looser: ?? text
    const loose = line.match(/^\?\?\s+(.+)/);
    if (loose) return { text: loose[1].trim(), tag: '', actor: '', priority: 'important' };
    return null;
  }
  return {
    text: m[1].trim(),
    tag: m[2],
    actor: m[3],
    priority: m[4] === '!!' ? 'blocking' : m[4] === '!' ? 'important' : 'nice-to-know',
  };
}

function parseAnsweredQuestion(line: string): DsnQuestion | null {
  // ✔✔ text #tag -> "answer"
  const m = line.match(/^✔✔\s+(.+?)\s+#(\S+)\s*->\s*"([^"]*)"/);
  if (!m) return null;
  return { text: m[1].trim(), tag: m[2], actor: '', priority: 'important', answered: m[3] };
}

function parseRejected(line: string): DsnRejected | null {
  // ✗✗ "what" -> "why" #tag
  const m = line.match(/^✗✗\s+"([^"]*)"\s*->\s*"([^"]*)"\s*#(\S+)/);
  if (!m) return null;
  return { proposal: m[1], reason: m[2], tag: m[3] };
}

function parseAssumption(line: string): DsnAssumption | null {
  // ~~ text #tag
  const m = line.match(/^~~\s+(.+?)\s+#(\S+)/);
  if (!m) {
    const loose = line.match(/^~~\s+(.+)/);
    if (loose) return { text: loose[1].trim(), tag: '', status: 'open' };
    return null;
  }
  return { text: m[1].trim(), tag: m[2], status: 'open' };
}

// ─── Helpers ─────────────────────────────────────────────────────────

function emptyDsnModel(): DsnModel {
  return {
    header: { context: '', session: '', focus: '', status: '', pending: '', started: '', updated: '' },
    narrative: '',
    history: [],
    actors: [],
    glossary: [],
    boundedContexts: [],
    systems: [],
    flow: [],
    variants: [],
    workObjects: [],
    businessRules: [],
    events: [],
    questions: [],
    rejected: [],
    assumptions: [],
  };
}

/**
 * Utility: strip DSN blocks (lines starting with `### %`, `### @`, `### [`, etc.)
 * from an AI response so the user only sees the conversational text.
 */
export function stripDsnFromText(text: string): string {
  if (!text) return text;
  return text
    .split('\n')
    .filter((line) => !line.startsWith('### '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
